<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card-text>
      <v-card class="mx-auto" elevation="0"
              :title="'Demographic baseline numbers - ' + this.dataset.title"
              ref="demographic-baseline-numbers">
        <v-row no-gutters>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <v-sheet class="pa-2 ma-2">
              <div class="number-box">
                <transition name="fade-bounce" mode="out-in">
                  <div :key="demographicBaselineTotalNumberOfPatients.value">
                    <v-progress-circular color="primary" indeterminate
                                         v-if="demographicBaselineTotalNumberOfPatients.value === undefined">
                    </v-progress-circular>
                    <span class="large-number">{{ demographicBaselineTotalNumberOfPatients.value }}</span>
                  </div>
                </transition>
                <div class="label large-number-label">{{ demographicBaselineTotalNumberOfPatients.label }}</div>
                <div class="label large-number-comment">{{ demographicBaselineTotalNumberOfPatients.comment }}</div>
              </div>
            </v-sheet>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <v-sheet class="pa-2 ma-2">
              <div class="number-box-left">
                <transition name="fade" mode="out-in">
                  <div :key="demographicBaselineNumbers">
                    <v-data-table :headers="demographicBaselineNumbersHeaders" :items="demographicBaselineNumbers"
                                  :fixed-header="true"
                                  height="200px"
                                  :items-per-page="0">
                      <template #bottom></template>
                      <template v-slot:[`item.percentage`]="{ item }">
                        <span style="color:#b2df8a">{{ item.percentage }}%</span>
                      </template>
                      <template v-slot:no-data>
                        <v-progress-circular color="primary" v-if="loading" indeterminate></v-progress-circular>
                      </template>
                    </v-data-table>
                  </div>
                </transition>
              </div>
            </v-sheet>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <v-sheet class="pa-2 ma-2">
              <div class="number-box">
                <div>
                  <v-progress-circular color="primary" indeterminate
                                       v-if="demographicBaselineTotalTimeSpanInYears.value === undefined">
                  </v-progress-circular>
                  <span class="large-number">{{ demographicBaselineTotalTimeSpanInYears.value }}</span>
                </div>
                <div class="label large-number-label">{{ demographicBaselineTotalTimeSpanInYears.label }}</div>
              </div>
            </v-sheet>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <v-sheet class="pa-2 ma-2">
              <div class="number-box-filtered">
                <transition name="fade-bounce" mode="out-in">
                  <div :key="filteredDemographicBaselineTotalNumberOfPatients.value">
                    <v-progress-circular color="primary" indeterminate
                                         v-if="filteredDemographicBaselineTotalNumberOfPatients.value === undefined">
                    </v-progress-circular>
                    <span class="large-number">{{ filteredDemographicBaselineTotalNumberOfPatients.value }}</span>
                    <span class="percentage-number" v-if="filteredDemographicBaselineTotalNumberOfPatients.percentage">&nbsp;
                              {{ filteredDemographicBaselineTotalNumberOfPatients.percentage }}%
                            </span>
                  </div>
                </transition>
                <div class="label large-number-label">{{ filteredDemographicBaselineTotalNumberOfPatients.label }}</div>
                <div class="label large-number-comment">{{ filteredDemographicBaselineTotalNumberOfPatients.comment }}</div>
              </div>
            </v-sheet>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <v-sheet class="pa-2 ma-2">
              <div class="tab-box-left-filtered">
                <v-tabs v-model="selected_filtered_demographic_baseline" align-tabs="start" show-arrows>
                  <v-tab key="filteredDemographicBaselinePieChartKey" value="filteredDemographicBaselinePieChartValue">
                    Pie chart
                  </v-tab>
                  <v-tab key="filteredDemographicBaselineTableKey" value="filteredDemographicBaselineTableValue">
                    Table
                  </v-tab>
                </v-tabs>
                <v-window v-model="selected_filtered_demographic_baseline">
                  <v-window-item key="filteredDemographicBaselinePieChartKey"
                                 value="filteredDemographicBaselinePieChartValue"
                                 transition="fade-transition"
                                 reverse-transition="fade-transition">
                    <div class="pie-chart">
                      <GChart type="PieChart"
                              @ready="ageGroupsChartReady"
                              :data="ageGroupsChartData"
                              :options="ageGroupsChartOptions"
                              v-if="ageGroupsChartData.length !== 0"/>
                      <v-chip variant="elevated" size="x-large" density="default" v-else>
                        No data to display. Please adjust your filters.
                      </v-chip>
                    </div>
                  </v-window-item>
                  <v-window-item key="filteredDemographicBaselineTableKey"
                                 value="filteredDemographicBaselineTableValue"
                                 transition="fade-transition"
                                 reverse-transition="fade-transition">
                    <div class="table-left-filtered">
                      <transition name="fade" mode="out-in">
                        <div :key="filteredDemographicBaselineNumbers">
                          <v-data-table :headers="demographicBaselineNumbersHeaders" :items="filteredDemographicBaselineNumbers"
                                        :fixed-header="true"
                                        height="350px"
                                        :items-per-page="0">
                            <template #bottom></template>
                            <template v-slot:[`item.percentage`]="{ item }">
                              <span style="color:#b2df8a">{{ item.percentage }}%</span>
                            </template>
                            <template v-slot:no-data>
                              <v-progress-circular color="primary" v-if="loading" indeterminate></v-progress-circular>
                            </template>
                          </v-data-table>
                        </div>
                      </transition>
                    </div>
                  </v-window-item>
                </v-window>
              </div>
            </v-sheet>
          </v-col>
          <!--
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <v-sheet class="pa-2 ma-2">
              <div class="number-box">
                <transition name="fade-bounce" mode="out-in">
                  <div :key="filteredDemographicBaselineTotalTimeSpanInYears.value">
                    <span class="large-number">{{ filteredDemographicBaselineTotalTimeSpanInYears.value }}</span>
                  </div>
                </transition>
                <div class="label large-number-label">{{ filteredDemographicBaselineTotalTimeSpanInYears.label }}</div>
              </div>
            </v-sheet>
          </v-col>
          -->
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <v-sheet class="pa-2 ma-2">
              <div class="pie-chart-box">
                <GChart type="PieChart"
                        @ready="sexesChartReady"
                        :data="sexesChartData"
                        :options="sexesChartOptions"
                        v-if="sexesChartData.length !== 0"/>
                <v-chip variant="elevated" size="x-large" density="default" v-else>
                  No data to display. Please adjust your filters.
                </v-chip>
              </div>
            </v-sheet>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col class="text-right">
            <vue-to-print :content="() => this.$refs['demographic-baseline-numbers'].$el"
                          :document-title="icd_or_atc.join('/') + '-' + 'demographic-baseline-numbers'"
                          remove-after-print>
              <template v-slot:trigger>
                <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
              </template>
            </vue-to-print>
          </v-col>
        </v-row>
      </v-card>
      <v-row no-gutters>
        <v-col cols="12" sm="10" md="8" lg="6" xl="6" xxl="6">
          <v-card class="mx-auto" min-height="800" elevation="0"
                  :title="this.measurementType.title + ((this.perX !== undefined) ? ' per year and ' +
                    this.perX.toLocaleString() + ' inhabitants' : ' per year')"
                  ref="number-of-diagnosed-patients">
            <v-row no-gutters>
              <v-col>
                <GChart type="BarChart"
                        @ready="totalNumberOfPatientsPerYearChartReady"
                        :data="totalNumberOfPatientsPerYearChartData"
                        :options="totalNumberOfPatientsPerYearChartOptions"
                        v-if="totalNumberOfPatientsPerYearChartData.length !== 0"/>
                <v-chip variant="elevated" size="x-large" density="default" v-else>
                  No data to display. Please adjust your filters.
                </v-chip>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['number-of-diagnosed-patients'].$el"
                              :document-title="icd_or_atc.join('/') + '-' + 'number-of-diagnosed-patients'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="12" sm="10" md="8" lg="6" xl="6" xxl="6">
          <v-card class="mx-auto" min-height="800" elevation="0"
                  :title="this.measurementType.title + ((this.perX !== undefined) ? ' per region and ' +
                    this.perX.toLocaleString() + ' inhabitants' : ' per region')">
            <GChart :settings="{ packages: ['geochart'], mapsApiKey: 'AIzaSyC95Y92u-OHlj8KHIODdWMCTqZe0WqZXZ4' }"
                    type="GeoChart"
                    @ready="regionalMapChartReady"
                    :data="regionalMapChartData"
                    :options="regionalMapChartOptions"
                    v-if="regionalMapChartData.length !== 0"/>
            <v-chip variant="elevated" size="x-large" density="default" v-else>
              No data to display. Please adjust your filters.
            </v-chip>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import {GChart} from 'vue-google-charts'
import axios from "axios";
import { VueToPrint } from "vue-to-print";

export default defineComponent({
  name: 'SocialstyrelsenGenericDemographicBaseline',
  props: {
    icd_or_atc: Array,
    dataset: {},
    measurementType: {},
    allAgesAtDiagnosis: Array,
    ageAtDiagnosis: Array,
    diagnosisDate: Array,
    sex: Array,
    region: Array,
    visible: Boolean,
    perX: {
      type: Number,
      default: undefined
    }
  },
  components: {
    GChart,
    VueToPrint
  },
  data: () => ({
    loading: false,
    demographicBaselineTotalNumberOfPatients: {},
    demographicBaselineTotalTimeSpanInYears: {},
    demographicBaselineNumbersHeaders: [],
    demographicBaselineNumbers: [],
    filteredDemographicBaselineTotalNumberOfPatients: {},
    filteredDemographicBaselineTotalTimeSpanInYears: {},
    filteredDemographicBaselineNumbers: [],
    selected_filtered_demographic_baseline: 'filteredDemographicBaselinePieChartValue',
    ageGroupsChartData: [],
    ageGroupsChartOptions: {
      title: 'Age groups',
      pieSliceText: 'value',
      is3D: false,
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 400,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: {
        position: 'end',
        alignment: 'end',
        textStyle: {color: '#c0c0c0', fontSize: 16},
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      }
    },
    sexesChartData: [],
    sexesChartOptions: {
      title: 'Sexes',
      pieSliceText: 'value',
      is3D: false,
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 400,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: {
        position: 'end',
        alignment: 'end',
        textStyle: {color: '#c0c0c0', fontSize: 16}
      }
    },
    totalNumberOfPatientsPerYearChartData: [],
    totalNumberOfPatientsPerYearChartOptions: {
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
      legend: {
        position: 'bottom',
        alignment: 'end',
        textStyle: {color: '#c0c0c0', fontSize: 16},
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      },
      hAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        textStyle: {
          color: '#ffffff'
        },
        gridlines: {
          color: '#c0c0c0'
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        },
        textStyle: {
          color: '#ffffff'
        }
      },
      annotations: {
        textStyle: {
          fontSize: 16,
          color: '#808080',
          auraColor: 'none'
        }
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      },
      isStacked: true
    },
    regionalMapChartData: [],
    regionalMapChartOptions: {
      region: 'SE',
      resolution: 'provinces',
      displayMode: 'regions',
      datalessRegionColor: '#364c5b',
      defaultColor: '#647e91',
      backgroundColor: '#364c5b',
      colorAxis: {colors: ['#b2df8a', '#c0c0c0', '#fb9a99']},
      height: 800,
      legend: 'none'
    }
  }),
  methods: {
    async loadDemographicBaselineTotalNumberOfPatientsData() {
      let data = axios.get('/socialstyrelsen-generic/demographic-baseline-total-number',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icds_or_atcs: this.icd_or_atc.join(','),
              dataset: this.dataset.value,
              measurement_type: this.measurementType.value,
              per_x: this.perX
            }
          });
      return data.then((response) => {
        this.demographicBaselineTotalNumberOfPatients = response.data;
      });
    },
    async loadDemographicBaselineTotalTimeSpanInYearsData() {
      let data = axios.get('/socialstyrelsen-generic/demographic-baseline-total-time-span-in-years',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icds_or_atcs: this.icd_or_atc.join(','),
              dataset: this.dataset.value
            }
          });
      return data.then((response) => {
        this.demographicBaselineTotalTimeSpanInYears = response.data;
      });
    },
    async loadDemographicBaselineData() {
      this.demographicBaselineNumbersHeaders = [
        {
          title: 'Group',
          align: 'start',
          key: 'label',
        },
        {
          title: this.measurementType.title + ((this.perX !== undefined) ? ' per ' + this.perX.toLocaleString() +
              ' inhabitants' : ''),
          align: 'end',
          key: 'value',
        }]
      let data = axios.get('/socialstyrelsen-generic/demographic-baseline',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icds_or_atcs: this.icd_or_atc.join(','),
              dataset: this.dataset.value,
              measurement_type: this.measurementType.value,
              age_at_diagnosis: this.allAgesAtDiagnosis.join(","),
              per_x: this.perX
            }
          });
      return data.then((response) => {
        this.demographicBaselineNumbers = response.data;
      });
    },
    async loadFilteredAgeGroupsData() {
      let data = axios.get('/socialstyrelsen-generic/filtered-age-groups',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icds_or_atcs: this.icd_or_atc.join(','),
              dataset: this.dataset.value,
              measurement_type: this.measurementType.value,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              sex: this.sex.join(","),
              region: this.region.join(","),
              per_x: this.perX
            }
          });
      return data.then((response) => {
        this.ageGroupsChartData = response.data;
      });
    },
    async loadFilteredSexesData() {
      let data = axios.get('/socialstyrelsen-generic/filtered-sexes',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icds_or_atcs: this.icd_or_atc.join(','),
              dataset: this.dataset.value,
              measurement_type: this.measurementType.value,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              sex: this.sex.join(","),
              region: this.region.join(","),
              per_x: this.perX
            }
          });
      return data.then((response) => {
        this.sexesChartData = response.data;
      });
    },
    async loadFilteredDemographicBaselineTotalNumberOfPatientsData() {
      let data = axios.get('/socialstyrelsen-generic/filtered-demographic-baseline-total-number',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icds_or_atcs: this.icd_or_atc.join(','),
              dataset: this.dataset.value,
              measurement_type: this.measurementType.value,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              sex: this.sex.join(","),
              region: this.region.join(","),
              per_x: this.perX
            }
          });
      return data.then((response) => {
        this.filteredDemographicBaselineTotalNumberOfPatients = response.data;
      });
    },
    async loadFilteredDemographicBaselineTotalTimeSpanInYearsData() {
      let data = axios.get('/socialstyrelsen-generic/filtered-demographic-baseline-total-time-span-in-years',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icds_or_atcs: this.icd_or_atc.join(','),
              dataset: this.dataset.value,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              sex: this.sex.join(","),
              region: this.region.join(",")
            }
          });
      return data.then((response) => {
        this.filteredDemographicBaselineTotalTimeSpanInYears = response.data;
      });
    },
    async loadFilteredDemographicBaselineData() {
      let data = axios.get('/socialstyrelsen-generic/filtered-demographic-baseline',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icds_or_atcs: this.icd_or_atc.join(','),
              dataset: this.dataset.value,
              measurement_type: this.measurementType.value,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              sex: this.sex.join(","),
              region: this.region.join(","),
              per_x: this.perX
            }
          });
      return data.then((response) => {
        this.filteredDemographicBaselineNumbers = response.data;
      });
    },
    async loadTotalNumberOfPatientsPerYearChartData() {
      let data = axios.get('/socialstyrelsen-generic/total-number-per-year-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icds_or_atcs: this.icd_or_atc.join(','),
              dataset: this.dataset.value,
              measurement_type: this.measurementType.value,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              sex: this.sex.join(","),
              region: this.region.join(","),
              per_x: this.perX
            }
          });
      return data.then((response) => {
        this.totalNumberOfPatientsPerYearChartData = response.data;
      });
    },
    async loadTotalNumberOfPatientsPerRegionChartData() {
      let data = axios.get('/socialstyrelsen-generic/total-number-per-region-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icds_or_atcs: this.icd_or_atc.join(','),
              dataset: this.dataset.value,
              measurement_type: this.measurementType.value,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              sex: this.sex.join(","),
              region: this.region.join(","),
              per_x: this.perX
            }
          });
      return data.then((response) => {
        this.regionalMapChartData = response.data;
      });
    },
    async ageGroupsChartReady(chart, google) {
      const setAgeGroupsChartSelection = (selectedRow) => {
        let selected_age_group = this.ageGroupsChartData[selectedRow + 1][0]
        if (selected_age_group !== undefined) {
          this.$emit('ageGroupSelected', selected_age_group)
        }
      }

      const ageGroupsSelectHandler = function () {
        const selection = chart.getSelection()
        if (selection.length === 0) {
          return
        }
        const selectedItem = selection[0];
        setAgeGroupsChartSelection(selectedItem.row)
      }

      google.visualization.events.addListener(chart, 'select', ageGroupsSelectHandler);
    },
    async regionalMapChartReady(chart, google) {
      const setRegionalMapChartSelection = (selectedRow) => {
        let selected_region = this.regionalMapChartData[selectedRow + 1][0].f
        if (selected_region !== undefined) {
          this.$emit('regionSelected', selected_region)
        }
      }

      const regionSelectHandler = function () {
        const selection = chart.getSelection()
        if (selection.length === 0) {
          return
        }
        const selectedItem = selection[0];
        setRegionalMapChartSelection(selectedItem.row)
      }

      google.visualization.events.addListener(chart, 'select', regionSelectHandler);
    },
    async sexesChartReady(chart, google) {
      const setSexesChartSelection = (selectedRow) => {
        let selected_sex = this.sexesChartData[selectedRow + 1][0]
        if (selected_sex !== undefined) {
          this.$emit('sexSelected', selected_sex)
        }
      }

      const sexesSelectHandler = function () {
        const selection = chart.getSelection()
        if (selection.length === 0) {
          return
        }
        const selectedItem = selection[0];
        setSexesChartSelection(selectedItem.row)
      }

      google.visualization.events.addListener(chart, 'select', sexesSelectHandler);
    },
    async totalNumberOfPatientsPerYearChartReady(chart, google) {
      const setTotalNumberOfPatientsPerYearChartChartSelection = (selectedRow) => {
        let selected_total_number_of_patients_per_year = this.totalNumberOfPatientsPerYearChartData[selectedRow + 1][0]
        if (selected_total_number_of_patients_per_year !== undefined) {
          this.$emit('totalNumberOfPatientsPerYearSelected', selected_total_number_of_patients_per_year)
        }
      }

      const totalNumberOfPatientsPerYearSelectHandler = function () {
        const selection = chart.getSelection()
        if (selection.length === 0) {
          return
        }
        const selectedItem = selection[0];
        setTotalNumberOfPatientsPerYearChartChartSelection(selectedItem.row)
      }

      google.visualization.events.addListener(chart, 'select', totalNumberOfPatientsPerYearSelectHandler);
    },
    async loadDynamicData() {
      return await Promise.all([
        this.loadFilteredDemographicBaselineTotalNumberOfPatientsData(),
        this.loadFilteredDemographicBaselineTotalTimeSpanInYearsData(),
        this.loadFilteredDemographicBaselineData(),
        this.loadFilteredAgeGroupsData(),
        this.loadFilteredSexesData(),
        this.loadTotalNumberOfPatientsPerYearChartData(),
        this.loadTotalNumberOfPatientsPerRegionChartData()
      ]).then(() => {
        console.log('Dynamic data loaded: SocialstyrelsenGenericDemographicBaseline')
      })
    }
  },
  async mounted() {
    this.loading = true
    await Promise.all([
      this.loadDemographicBaselineTotalNumberOfPatientsData(),
      this.loadDemographicBaselineTotalTimeSpanInYearsData(),
      this.loadDemographicBaselineData()
    ]).then(() => {
      return this.loadDynamicData()
    }).finally(() => {
      this.loading = false
    })
  },
  updated() {
    if (this.visible) {
      this.loading = true
      Promise.all([
        this.loadDemographicBaselineTotalNumberOfPatientsData(),
        this.loadDemographicBaselineTotalTimeSpanInYearsData(),
        this.loadDemographicBaselineData()
      ]).then(() => {
        return this.loadDynamicData()
      }).finally(() => {
        this.loading = false
      })
    }
  }
});
</script>

<style scoped>
.number-box {
  height: 250px;
  justify-content: space-around;
  text-align: center;
  padding: 50px;
  margin: 10px;
  flex: 1;
  border-radius: 8px;
  background-color: #364c5b;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px #c0c0c0; /* Elevated border effect */
}

.number-box-left {
  height: 250px;
  justify-content: space-around;
  text-align: left;
  padding: 20px;
  margin: 10px;
  flex: 1;
  border-radius: 8px;
  background-color: #364c5b;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px #c0c0c0; /* Elevated border effect */
}

.number-box-filtered {
  height: 450px;
  justify-content: space-around;
  text-align: center;
  padding: 160px;
  margin: 10px;
  flex: 1;
  border-radius: 8px;
  background-color: #364c5b;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px #c0c0c0; /* Elevated border effect */
}

.tab-box-left-filtered {
  height: 450px;
  text-align: left;
  margin: 10px;
  border-radius: 8px;
  background-color: #364c5b;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px #c0c0c0; /* Elevated border effect */
}

.table-left-filtered {
  justify-content: space-around;
  text-align: left;
  padding: 20px;
}

.pie-chart {
  text-align: center;
}

.pie-chart-box {
  height: 450px;
  text-align: center;
  padding: 20px;
  margin: 10px;
  border-radius: 8px;
  background-color: #364c5b;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px #c0c0c0; /* Elevated border effect */
}

.large-number {
  font-size: 4em;
  font-weight: bold;
}

.percentage-number {
  font-size: 2em;
  color: #b2df8a;
}

.large-number-label {
  font-size: 1em;
  margin-top: 10px;
}

.large-number-comment {
  font-size: 0.8em;
  margin-top: 10px;
  margin-left: 20px;
  margin-right: 20px;
}

.fade-bounce-enter-active {
  transition: all 0.5s ease-in;
  animation: bounce-in 0.5s;
}
.fade-bounce-leave-active {
  transition: all 0.5s ease-out;
  animation: bounce-in 0.5s reverse;
}

.fade-bounce-enter-from,
.fade-bounce-leave-to {
  opacity: 0;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.25);
  }
  100% {
    transform: scale(1);
  }
}

@media print {
  .mx-auto {
    zoom: 50%;
  }
}
</style>
